import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import type { UserProfile } from '@/features/user/types/types';
import { COUNTRY_CODES, GENDER_OPTIONS } from '@/features/user/constants';
import { formatDate, parsePhoneNumber, getPhonePrefix, isValidPhoneNumber } from '@/lib/utils';
import FormInput from './forms/FormInput';
import GenderSelector from './forms/GenderSelector';
import CalendarPicker from './forms/CalendarPicker';
import { UserCircleIcon, CalendarIcon, GenderIcon, PhoneIcon, PencilIcon } from './icons/ProfileIcons';

interface ProfileDetailsProps {
  user: UserProfile;
  setUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  onSaveChanges?: (userData: UserProfile) => void;
}

const ProfileDetails: React.FC<ProfileDetailsProps> = ({ 
  user, 
  setUser, 
  isEditing, 
  setIsEditing,
  onSaveChanges
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "phone_number") {
      setUser(prev => ({ 
        ...prev, 
        phone_number: { ...prev.phone_number, number: value } 
      }));
    } else {
      setUser(prev => ({ 
        ...prev, 
        [name]: value 
      }));
    }
  };
  
  const [countryCodeInput, setCountryCodeInput] = useState('');

  // Update the input when user data changes
  useEffect(() => {
    setCountryCodeInput(getPhonePrefixFromCountryCode(user.phone_number.country_code));
  }, [user.phone_number.country_code]);

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setCountryCodeInput(inputValue);
    
    // If user types a phone number with country prefix (e.g., +44), parse it
    if (inputValue.startsWith('+')) {
      try {
        const parsed = parsePhoneNumber(inputValue);
        setUser(prev => ({ 
          ...prev, 
          phone_number: { 
            country_code: parsed.country_code, 
            number: prev.phone_number.number // Keep existing number
          } 
        }));
      } catch {
        // If parsing fails, just update the display value
        console.warn('Could not parse country prefix:', inputValue);
      }
    } else if (inputValue.trim() !== '') {
      // If user enters just country code (e.g., GB), keep it as is
      setUser(prev => ({ 
        ...prev, 
        phone_number: { ...prev.phone_number, country_code: inputValue } 
      }));
    } else {
      // If input is empty, set default country code
      setUser(prev => ({ 
        ...prev, 
        phone_number: { ...prev.phone_number, country_code: 'GB' } 
      }));
    }
  };

  const handleSaveChanges = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone number
    const fullPhoneNumber = `${getPhonePrefixFromCountryCode(user.phone_number.country_code)}${user.phone_number.number}`;
    if (!isValidPhoneNumber(fullPhoneNumber, user.phone_number.country_code)) {
      alert('Please enter a valid phone number');
      return;
    }
    
    if (onSaveChanges) {
      onSaveChanges(user);
    } else {
      console.log("Saving data:", user);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    // TODO: Reset to original data
    setIsEditing(false);
  };

  const getGenderString = (genderCode: string) => 
    GENDER_OPTIONS.find(option => option.value === genderCode)?.label || "Not specified";
  
  
  const getPhonePrefixFromCountryCode = (countryCode: string) => 
    COUNTRY_CODES.find(c => c.value === countryCode)?.label || getPhonePrefix(countryCode);

  return (
    <motion.div 
      className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-3xl p-8"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ 
        y: -2,
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
        transition: { type: "spring", stiffness: 300, damping: 30 }
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.8
      }}
    >
      <motion.div 
        className="flex items-center justify-between mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <div>
          <motion.h3 
            className="text-2xl font-serif font-medium"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            Personal Details
          </motion.h3>
          <motion.div 
            className="w-16 h-0.5 bg-violet-500 mt-2"
            initial={{ width: 0 }}
            animate={{ width: 64 }}
            transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
          />
        </div>
        <AnimatePresence>
          {!isEditing && (
            <motion.button 
              onClick={() => setIsEditing(true)} 
              className="w-10 h-10 bg-gray-800 hover:bg-violet-500/20 rounded-full flex items-center justify-center group"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              whileHover={{ 
                scale: 1.1,
                backgroundColor: "rgba(139, 92, 246, 0.2)",
                transition: { type: "spring", stiffness: 400, damping: 10 }
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ 
                delay: 0.4, 
                type: "spring", 
                stiffness: 300, 
                damping: 20 
              }}
            >
              <motion.div
                whileHover={{ rotate: 15 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <PencilIcon className="w-5 h-5 text-gray-400 group-hover:text-violet-400 transition-colors duration-300" />
              </motion.div>
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>
      
      <form onSubmit={handleSaveChanges}>
        <AnimatePresence mode="wait">
          {isEditing ? (
            <motion.div 
              key="edit-mode"
              className="space-y-8"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ 
                duration: 0.4, 
                ease: "easeInOut",
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
            >
              {/* Name Section */}
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                <motion.h4 
                  className="text-lg font-medium text-gray-200 border-b border-gray-800/50 pb-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  Personal Information
                </motion.h4>
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                  >
                    <FormInput 
                      label="First Name" 
                      id="first_name" 
                      name="first_name" 
                      type="text" 
                      value={user.first_name} 
                      onChange={handleChange} 
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <FormInput 
                      label="Last Name" 
                      id="last_name" 
                      name="last_name" 
                      type="text" 
                      value={user.last_name} 
                      onChange={handleChange} 
                    />
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Date and Gender Section */}
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                <motion.h4 
                  className="text-lg font-medium text-gray-200 border-b border-gray-800/50 pb-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  Additional Details
                </motion.h4>
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <CalendarPicker 
                      label="Date of Birth" 
                      id="dob" 
                      name="dob" 
                      value={user.dob} 
                      onChange={(value) => {
                        const event = { target: { name: 'dob', value } } as React.ChangeEvent<HTMLInputElement>;
                        handleChange(event);
                      }} 
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                  >
                    <GenderSelector 
                      value={user.gender} 
                      onChange={(value) => {
                        const event = { target: { name: 'gender', value: value.toString() } } as React.ChangeEvent<HTMLInputElement>;
                        handleChange(event);
                      }}
                    />
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Contact Section */}
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <motion.h4 
                  className="text-lg font-medium text-gray-200 border-b border-gray-800/50 pb-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  Contact Information
                </motion.h4>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                >
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Phone Number
                  </label>
                  <div className="flex gap-3">
                    <motion.div 
                      className="w-20"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6, duration: 0.4 }}
                    >
                      <input 
                        type="text" 
                        name="country_code" 
                        value={countryCodeInput} 
                        onChange={handleCountryCodeChange} 
                        placeholder="+44"
                        className="w-full bg-gray-900/50 border border-gray-700 rounded-lg py-2.5 px-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all duration-300 text-center" 
                      />
                    </motion.div>
                    <motion.div 
                      className="flex-1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7, duration: 0.4 }}
                    >
                      <input 
                        type="tel" 
                        name="phone_number" 
                        value={user.phone_number.number} 
                        onChange={handleChange} 
                        placeholder="Enter phone number"
                        className="w-full bg-gray-900/50 border border-gray-700 rounded-lg py-2.5 px-4 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all duration-300" 
                      />
                    </motion.div>
                  </div>
                  <AnimatePresence>
                    {user.phone_number.country_code && user.phone_number.number && (
                      <motion.div 
                        className="mt-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                      >
                        {(() => {
                          const fullNumber = `${getPhonePrefixFromCountryCode(user.phone_number.country_code)}${user.phone_number.number}`;
                          const isValid = isValidPhoneNumber(fullNumber, user.phone_number.country_code);
                          return (
                            <motion.p 
                              className={`text-xs ${isValid ? 'text-green-400' : 'text-red-400'}`}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.2, duration: 0.3 }}
                            >
                              {isValid ? '✓ Valid phone number' : '✗ Invalid phone number format'}
                            </motion.p>
                          );
                        })()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            </motion.div>
        ) : (
          <motion.div 
            key="view-mode"
            className="space-y-2"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ 
              duration: 0.4, 
              ease: "easeInOut",
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
          >
            {/* Full Name Item */}
            <motion.div 
              className="flex items-center justify-between p-4 border-b border-gray-800/50"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              whileHover={{ 
                backgroundColor: "rgba(139, 92, 246, 0.05)",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <UserCircleIcon className="w-6 h-6 text-violet-400" />
                </motion.div>
                <p className="text-sm text-gray-400">Full Name</p>
              </div>
              <motion.p 
                className="text-lg font-medium text-gray-50"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {user.first_name} {user.last_name}
              </motion.p>
            </motion.div>

            {/* Date of Birth Item */}
            <motion.div 
              className="flex items-center justify-between p-4 border-b border-gray-800/50"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              whileHover={{ 
                backgroundColor: "rgba(139, 92, 246, 0.05)",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <CalendarIcon className="w-6 h-6 text-violet-400" />
                </motion.div>
                <p className="text-sm text-gray-400">Date of Birth</p>
              </div>
              <motion.p 
                className="text-lg font-medium text-gray-50"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                {formatDate(user.dob)}
              </motion.p>
            </motion.div>

            {/* Gender Item */}
            <motion.div 
              className="flex items-center justify-between p-4 border-b border-gray-800/50"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              whileHover={{ 
                backgroundColor: "rgba(139, 92, 246, 0.05)",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <GenderIcon className="w-6 h-6 text-violet-400" />
                </motion.div>
                <p className="text-sm text-gray-400">Gender</p>
              </div>
              <motion.p 
                className="text-lg font-medium text-gray-50"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
              >
                {getGenderString(user.gender)}
              </motion.p>
            </motion.div>

            {/* Phone Number Item */}
            <motion.div 
              className="flex items-center justify-between p-4 border-b border-gray-800/50"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              whileHover={{ 
                backgroundColor: "rgba(139, 92, 246, 0.05)",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <PhoneIcon className="w-6 h-6 text-violet-400" />
                </motion.div>
                <p className="text-sm text-gray-400">Phone Number</p>
              </div>
              <motion.p 
                className="text-lg font-medium text-gray-50"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                {getPhonePrefixFromCountryCode(user.phone_number.country_code)} {user.phone_number.number}
              </motion.p>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        <AnimatePresence>
          {isEditing && (
            <motion.div 
              className="flex items-center justify-end gap-4 mt-8 pt-8 border-t border-gray-800"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.4, 
                ease: "easeInOut",
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
            >
              <motion.button 
                type="button" 
                onClick={handleCancel} 
                className="px-6 py-3 border border-gray-700 rounded-full font-medium hover:bg-white/10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ 
                  scale: 1.05,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderColor: "rgb(156, 163, 175)",
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ 
                  delay: 0.1, 
                  duration: 0.4,
                  type: "spring",
                  stiffness: 300,
                  damping: 20
                }}
              >
                Cancel
              </motion.button>
              <motion.button 
                type="submit" 
                className="px-6 py-3 bg-violet-600 text-white border border-violet-500 rounded-full font-medium hover:bg-violet-500"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ 
                  scale: 1.05,
                  backgroundColor: "rgb(124, 58, 237)",
                  boxShadow: "0 0 20px rgba(139, 92, 246, 0.4)",
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ 
                  delay: 0.2, 
                  duration: 0.4,
                  type: "spring",
                  stiffness: 300,
                  damping: 20
                }}
              >
                Save Changes
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </motion.div>
  );
};

export default ProfileDetails;
